/**
 * API client for subscriptions management (CTX-26).
 *
 * Talks to the endpoints defined by CTX-208. While that ticket is in flight
 * the module can serve local fixtures instead — set
 * `REACT_APP_MOCK_SUBSCRIPTIONS=true` in `.env.local`. The switch mirrors the
 * BGP Visibility precedent (`REACT_APP_MOCK_BGP`): the env var defaults to `''`
 * in `rsbuild.config.ts`, so production inlines `'' === 'true'` → false and
 * dead-code-eliminates the fixture branch together with its dynamic import.
 */
import type { AxiosInstance } from "axios";
import type {
	AvailableServicesResponse,
	CreatedSubscription,
	CreateSubscriptionPayload,
	RenewSubscriptionPayload,
	SubscriptionChainResponse,
	SubscriptionListQuery,
	SubscriptionListResponse,
} from "../types";

const MOCK_SUBSCRIPTIONS = process.env.REACT_APP_MOCK_SUBSCRIPTIONS === "true";

const SUBSCRIPTIONS_MOUNT = "/kontroledge/subscriptions";

/**
 * The backend wraps every payload as `{ success, message, data }`. Unwrap it
 * here so callers never deal with the envelope, and fail loudly when the
 * shape is not what we expect instead of surfacing `undefined` downstream.
 */
function unwrap<T>(body: unknown, context: string): T {
	const envelope = body as { success?: boolean; message?: string; data?: T };
	if (!envelope || typeof envelope !== "object" || envelope.data === undefined) {
		throw new Error(`${context}: unexpected response shape`);
	}
	if (envelope.success === false) {
		throw new Error(envelope.message || `${context}: request failed`);
	}
	return envelope.data;
}

/**
 * Serialize the table's query into request params. Repeated values go as
 * comma-joined strings, which is what the other KE list endpoints accept;
 * empty selections are omitted entirely so the backend applies no filter.
 */
function toParams(query: SubscriptionListQuery): Record<string, string | number> {
	const params: Record<string, string | number> = {
		page: query.page,
		pageSize: query.pageSize,
	};
	if (query.search?.trim()) params.search = query.search.trim();
	if (query.serviceTypes?.length) params.serviceType = query.serviceTypes.join(",");
	if (query.serviceNames?.length) params.serviceName = query.serviceNames.join(",");
	if (query.states?.length) params.state = query.states.join(",");
	if (query.tenantIds?.length) params.tenantId = query.tenantIds.join(",");
	if (query.sortBy) params.sortBy = query.sortBy;
	if (query.sortDir) params.sortDir = query.sortDir;
	return params;
}

/** `GET /kontroledge/subscriptions` — one page of the table. */
export async function listSubscriptions(
	api: AxiosInstance,
	query: SubscriptionListQuery
): Promise<SubscriptionListResponse> {
	if (MOCK_SUBSCRIPTIONS) {
		const { mockListSubscriptions } = await import("./subscriptionsFixtures");
		return mockListSubscriptions(query);
	}
	const response = await api.get(SUBSCRIPTIONS_MOUNT, { params: toParams(query) });
	return unwrap<SubscriptionListResponse>(response.data, "listSubscriptions");
}

/**
 * `GET /kontroledge/subscriptions/:subscriptionId/chain` — the full renewal
 * chain of the service this subscription belongs to, plus its audit trail.
 */
export async function getSubscriptionChain(
	api: AxiosInstance,
	subscriptionId: string
): Promise<SubscriptionChainResponse> {
	if (MOCK_SUBSCRIPTIONS) {
		const { mockGetSubscriptionChain } = await import("./subscriptionsFixtures");
		return mockGetSubscriptionChain(subscriptionId);
	}
	const response = await api.get(`${SUBSCRIPTIONS_MOUNT}/${subscriptionId}/chain`);
	return unwrap<SubscriptionChainResponse>(response.data, "getSubscriptionChain");
}

/**
 * `POST /kontroledge/subscriptions/:subscriptionId/renew` — already shipped by
 * CTX-23. Creates the next subscription in the chain; when the current term is
 * still running this is an early renewal and the service's effective validity
 * moves forward without interruption.
 */
export async function renewSubscription(
	api: AxiosInstance,
	subscriptionId: string,
	payload: RenewSubscriptionPayload
): Promise<void> {
	if (MOCK_SUBSCRIPTIONS) {
		const { mockRenewSubscription } = await import("./subscriptionsFixtures");
		return mockRenewSubscription(subscriptionId, payload);
	}
	const response = await api.post(
		`${SUBSCRIPTIONS_MOUNT}/${subscriptionId}/renew`,
		payload
	);
	unwrap<unknown>(response.data, "renewSubscription");
}

/**
 * `POST /kontroledge/subscriptions` — manual creation (CTX-208).
 *
 * The escape hatch for a service the ordering flow left without a
 * subscription. The backend refuses to stack a term on top of existing
 * coverage (409) and hides services outside the caller's tenants behind the
 * same 404 as a missing one.
 */
export async function createSubscription(
	api: AxiosInstance,
	payload: CreateSubscriptionPayload
): Promise<CreatedSubscription> {
	if (MOCK_SUBSCRIPTIONS) {
		const { mockCreateSubscription } = await import("./subscriptionsFixtures");
		return mockCreateSubscription(payload);
	}
	const response = await api.post(SUBSCRIPTIONS_MOUNT, payload);
	return unwrap<CreatedSubscription>(response.data, "createSubscription");
}

/**
 * `GET /kontroledge/subscriptions/available-services` — services nothing
 * covers today, so the creation form can offer a picker instead of asking for
 * a raw database id.
 */
export async function listAvailableServices(
	api: AxiosInstance,
	params: { serviceType?: string; search?: string; limit?: number }
): Promise<AvailableServicesResponse> {
	if (MOCK_SUBSCRIPTIONS) {
		const { mockListAvailableServices } = await import("./subscriptionsFixtures");
		return mockListAvailableServices(params);
	}
	const response = await api.get(`${SUBSCRIPTIONS_MOUNT}/available-services`, {
		params,
	});
	return unwrap<AvailableServicesResponse>(response.data, "listAvailableServices");
}
